/**
 * Sample itineraries for the signed-in Itineraries screen.
 * Replace this module when the itinerary API is ready.
 * List, map, stats, chart, segments, route, navigation, launches, and settings each have a skeleton.
 */

export type ItineraryStatus = 'ready' | 'draft' | 'locked';
export type TravelMode = 'walking' | 'driving' | 'point';
export type SpeedUnit = 'kmh' | 'mph';
export type PlaybackMarker = 'maps' | 'car' | 'waze';
export type PanelSize = 's' | 'm' | 'l';
export type Maneuver =
  | 'start'
  | 'left'
  | 'right'
  | 'uturn'
  | 'bear-right'
  | 'bear-left'
  | 'straight'
  | 'arrive';
export type ItineraryChip = 'all' | 'recent' | 'drafts' | 'locked';
export type ItinerarySort = 'board' | 'edited' | 'name' | 'distance' | 'launches';
export type TravelFilter = 'any' | TravelMode;

export interface MapPoint {
  x: number;
  y: number;
}

export interface MapLabel {
  x: number;
  y: number;
  text: string;
  sub?: string;
}

export interface Segment {
  id: string;
  fromLabel: string;
  toLabel: string;
  place: string;
  distanceKm: number;
  durationMin: number;
  avgSpeedKmh: number;
  fromIndex: number;
  toIndex: number;
}

export interface NavStep {
  id: string;
  maneuver: Maneuver;
  text: string;
  distanceLabel: string;
}

export interface Launch {
  id: string;
  atLabel: string;
  durationMin: number;
  distanceKm: number;
  avgSpeedKmh: number;
  device: string;
  appVersion: string;
  status: 'completed';
}

export interface ElevationPoint {
  km: number;
  meters: number;
}

export interface ItinerarySettings {
  showRouteDetails: boolean;
  travelMode: TravelMode;
  defaultSpeedKmh: number;
  speedUnit: SpeedUnit;
  accuracyM: number;
  routeWidth: number;
  marker: PlaybackMarker;
  color: RouteColor;
  panel: PanelSize;
}

export interface Itinerary {
  id: string;
  name: string;
  description: string;
  status: ItineraryStatus;
  lockReason?: string;
  recent: boolean;
  dateLabel: string;
  editedAt: number;
  boardRank: number;
  path: MapPoint[];
  labels: MapLabel[];
  segments: Segment[];
  steps: NavStep[];
  launches: Launch[];
  /** Visual bar heights for the 30-day chart. Not a partition of the launch table. */
  launchBars: number[];
  elevation: ElevationPoint[];
  settings: ItinerarySettings;
}

export const PAGE_SIZE = 6;

export const LAUNCH_LABELS = ['1 Oct', '8 Oct', '15 Oct', '22 Oct', '29 Oct'] as const;

/** Design clock. New drafts use this day so the mock catalog does not drift. */
export const ITINERARY_NOW = Date.parse('2026-10-25T10:47:00+05:30');
export const ITINERARY_TODAY = '25 Oct 2026';

export const ROUTE_COLOR_OPTIONS = [
  { id: 'blue', label: 'Blue', hex: '#2563eb' },
  { id: 'red', label: 'Red', hex: '#ef4444' },
  { id: 'orange', label: 'Orange', hex: '#f97316' },
  { id: 'green', label: 'Green', hex: '#22c55e' },
  { id: 'purple', label: 'Purple', hex: '#a855f7' },
  { id: 'pink', label: 'Pink', hex: '#ec4899' },
  { id: 'brown', label: 'Brown', hex: '#92400e' },
  { id: 'slate', label: 'Slate', hex: '#334155' },
] as const;

export type RouteColor = (typeof ROUTE_COLOR_OPTIONS)[number]['id'];

export function routeHex(color: RouteColor): string {
  const match = ROUTE_COLOR_OPTIONS.find((option) => option.id === color);
  return match ? match.hex : '#2563eb';
}

export function formatKm(km: number): string {
  const rounded = Math.round(km * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} km`;
}

export function formatMinutes(min: number): string {
  const rounded = Math.round(min * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} min`;
}

export function formatSpeed(kmh: number, unit: SpeedUnit): string {
  const value = unit === 'mph' ? kmh * 0.621371 : kmh;
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return unit === 'mph' ? `${text} mph` : `${text} km/h`;
}

export function formatSeg(count: number): string {
  return `${count} seg`;
}

export function formatLaunches(count: number): string {
  return `${count} ${count === 1 ? 'launch' : 'launches'}`;
}

export function formatMeters(meters: number): string {
  if (meters >= 1000) {
    const km = Math.round((meters / 1000) * 10) / 10;
    const text = Number.isInteger(km) ? String(km) : km.toFixed(1);
    return `${text} km`;
  }
  return `${Math.max(0, Math.round(meters))} m`;
}

export function modeLabel(mode: TravelMode): string {
  if (mode === 'walking') return 'Walking';
  if (mode === 'point') return 'Point to point';
  return 'Driving';
}

export function totalDistance(item: Itinerary): number {
  const sum = item.segments.reduce((total, segment) => total + segment.distanceKm, 0);
  return Math.round(sum * 10) / 10;
}

export function totalDuration(item: Itinerary): number {
  return item.segments.reduce((total, segment) => total + segment.durationMin, 0);
}

export function meanSpeed(launches: Launch[]): number {
  if (launches.length === 0) return 0;
  const total = launches.reduce((sum, launch) => sum + launch.avgSpeedKmh, 0);
  return Math.round(total / launches.length);
}

export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

export function segmentTitle(segment: Segment): string {
  return `${segment.fromLabel} → ${segment.toLabel}`;
}

export function launchInsight(item: Itinerary): string {
  const count = item.launches.length;
  if (count === 0) {
    return 'This itinerary has not been launched in the last 30 days.';
  }
  const distance = item.launches.reduce((sum, launch) => sum + launch.distanceKm, 0);
  const duration = item.launches.reduce((sum, launch) => sum + launch.durationMin, 0) / count;
  const times = count === 1 ? 'time' : 'times';
  return `This itinerary was launched ${count} ${times} in the last 30 days with a total distance of ${formatKm(distance)}. Average duration was ${formatMinutes(duration)} with an average speed of ${meanSpeed(item.launches)} km/h.`;
}

export function routeSummary(item: Itinerary): string {
  const count = item.segments.length;
  const noun = count === 1 ? 'segment' : 'segments';
  const start = item.segments[0]?.place ?? 'the start';
  const end = item.segments[count - 1]?.place ?? 'the end';
  return `This itinerary has ${count} ${noun} with a total distance of ${formatKm(totalDistance(item))}. Estimated duration is ${formatMinutes(totalDuration(item))} with an average speed of ${formatSpeed(item.settings.defaultSpeedKmh, item.settings.speedUnit)}. Route starts from ${start} and ends at ${end}.`;
}

export function buildSteps(segments: Segment[]): NavStep[] {
  const steps: NavStep[] = [];
  segments.forEach((segment, index) => {
    const meters = Math.round(segment.distanceKm * 1000);
    if (index === 0) {
      steps.push({
        id: `${segment.id}-start`,
        maneuver: 'start',
        text: `Start at ${segment.place}.`,
        distanceLabel: formatMeters(Math.max(12, Math.round(meters * 0.22))),
      });
    }
    const maneuver: Maneuver = index % 2 === 0 ? 'right' : 'left';
    steps.push({
      id: `${segment.id}-turn`,
      maneuver,
      text: maneuver === 'left' ? 'Turn left.' : 'Turn right.',
      distanceLabel: formatMeters(Math.max(12, Math.round(meters * 0.7))),
    });
    if (index === segments.length - 1) {
      steps.push({
        id: `${segment.id}-end`,
        maneuver: 'arrive',
        text: 'Your destination is on the left.',
        distanceLabel: '0 m',
      });
    }
  });
  return steps;
}

function point(x: number, y: number): MapPoint {
  return { x, y };
}

function elevationFor(km: number, seed: number): ElevationPoint[] {
  const steps = 7;
  return Array.from({ length: steps }, (_, index) => {
    const at = (km * index) / (steps - 1);
    const meters = Math.round(190 + Math.sin(seed + index * 0.85) * 26 + index * 7);
    return { km: Math.round(at * 10) / 10, meters };
  });
}

function barsFor(count: number, seed: number): number[] {
  const bars = [0, 0, 0, 0, 0];
  for (let index = 0; index < count; index += 1) {
    bars[(seed + index * 2) % bars.length] += 1;
  }
  return bars;
}

const LAUNCH_TIMES = [
  '7 Oct 2026, 10:24 AM',
  '3 Oct 2026, 06:12 PM',
  '28 Sep 2026, 09:18 AM',
  '21 Sep 2026, 07:45 PM',
  '12 Sep 2026, 08:05 AM',
  '2 Sep 2026, 06:40 PM',
];

interface SegmentSeed {
  fromLabel: string;
  toLabel: string;
  place: string;
  distanceKm: number;
  durationMin: number;
  avgSpeedKmh: number;
  fromIndex?: number;
  toIndex?: number;
}

interface LaunchSeed {
  atLabel: string;
  durationMin: number;
  distanceKm: number;
  avgSpeedKmh: number;
  device: string;
  appVersion: string;
}

interface ItinerarySeed {
  id: string;
  name: string;
  description?: string;
  status: ItineraryStatus;
  lockReason?: string;
  recent?: boolean;
  dateLabel: string;
  editedAt: string;
  boardRank: number;
  color: RouteColor;
  speed: number;
  mode?: TravelMode;
  accuracy?: number;
  width?: number;
  marker?: PlaybackMarker;
  panel?: PanelSize;
  unit?: SpeedUnit;
  path: MapPoint[];
  labels?: MapLabel[];
  segments: SegmentSeed[];
  steps?: Array<Omit<NavStep, 'id'>>;
  launches?: LaunchSeed[];
  launchCount?: number;
  device?: string;
  bars?: number[];
  elevation?: ElevationPoint[];
}

function buildItinerary(seed: ItinerarySeed): Itinerary {
  const last = Math.max(1, seed.path.length - 1);
  const segments: Segment[] = seed.segments.map((segment, index) => {
    const count = seed.segments.length;
    let fromIndex = segment.fromIndex ?? Math.round((last * index) / count);
    let toIndex = segment.toIndex ?? Math.round((last * (index + 1)) / count);
    if (fromIndex >= last) fromIndex = Math.max(0, last - 1);
    if (toIndex <= fromIndex) toIndex = Math.min(last, fromIndex + 1);
    return {
      id: `${seed.id}-seg-${index + 1}`,
      fromLabel: segment.fromLabel,
      toLabel: segment.toLabel,
      place: segment.place,
      distanceKm: segment.distanceKm,
      durationMin: segment.durationMin,
      avgSpeedKmh: segment.avgSpeedKmh,
      fromIndex,
      toIndex,
    };
  });

  const launchSeeds: LaunchSeed[] = seed.launches
    ?? Array.from({ length: seed.launchCount ?? 0 }, (_, index) => ({
      atLabel: LAUNCH_TIMES[index % LAUNCH_TIMES.length],
      durationMin: 8 + index,
      distanceKm: segments.reduce((sum, segment) => sum + segment.distanceKm, 0),
      avgSpeedKmh: seed.speed,
      device: seed.device ?? 'Samsung F42',
      appVersion: 'v1.2.3',
    }));

  const launches: Launch[] = launchSeeds.map((launch, index) => ({
    id: `${seed.id}-launch-${index + 1}`,
    atLabel: launch.atLabel,
    durationMin: launch.durationMin,
    distanceKm: launch.distanceKm,
    avgSpeedKmh: launch.avgSpeedKmh,
    device: launch.device,
    appVersion: launch.appVersion,
    status: 'completed',
  }));

  const distance = Math.round(segments.reduce((sum, segment) => sum + segment.distanceKm, 0) * 10) / 10;
  const labels = seed.labels ?? [
    {
      x: seed.path[0]?.x ?? 20,
      y: Math.max(8, (seed.path[0]?.y ?? 20) - 10),
      text: segments[0]?.place.split(',')[0] ?? seed.name,
    },
  ];

  return {
    id: seed.id,
    name: seed.name,
    description: seed.description?.trim() ?? '',
    status: seed.status,
    lockReason: seed.lockReason,
    recent: Boolean(seed.recent),
    dateLabel: seed.dateLabel,
    editedAt: Date.parse(seed.editedAt),
    boardRank: seed.boardRank,
    path: seed.path,
    labels,
    segments,
    steps: seed.steps
      ? seed.steps.map((step, index) => ({ ...step, id: `${seed.id}-step-${index + 1}` }))
      : buildSteps(segments),
    launches,
    launchBars: seed.bars ?? barsFor(launches.length, seed.boardRank),
    elevation: seed.elevation ?? elevationFor(distance, seed.boardRank),
    settings: {
      showRouteDetails: true,
      travelMode: seed.mode ?? 'driving',
      defaultSpeedKmh: seed.speed,
      speedUnit: seed.unit ?? 'kmh',
      accuracyM: seed.accuracy ?? 28,
      routeWidth: seed.width ?? 11,
      marker: seed.marker ?? 'maps',
      color: seed.color,
      panel: seed.panel ?? 'm',
    },
  };
}

const NEW_ONE_STEPS: Array<Omit<NavStep, 'id'>> = [
  { maneuver: 'start', text: 'Start at Patparganj Rd, New Delhi.', distanceLabel: '431 m' },
  { maneuver: 'left', text: 'Turn left.', distanceLabel: '16 m' },
  { maneuver: 'uturn', text: 'Make a right U-turn, if permitted.', distanceLabel: '306 m' },
  { maneuver: 'bear-right', text: 'Bear right onto Mandawali Main Road.', distanceLabel: '1.7 km' },
  { maneuver: 'right', text: 'Turn right onto Shri Hanuman Marg.', distanceLabel: '154 m' },
  { maneuver: 'right', text: 'Turn right.', distanceLabel: '126 m' },
  { maneuver: 'left', text: 'Turn left onto Madhu Vihar Road.', distanceLabel: '408 m' },
  { maneuver: 'right', text: 'Turn right to stay on Madhu Vihar Road.', distanceLabel: '374 m' },
  { maneuver: 'left', text: 'Turn left.', distanceLabel: '23 m' },
  { maneuver: 'left', text: 'Turn left.', distanceLabel: '97 m' },
  { maneuver: 'left', text: 'Turn left.', distanceLabel: '27 m' },
  { maneuver: 'arrive', text: 'Your destination is on the left.', distanceLabel: '0 m' },
];

export const ITINERARIES: Itinerary[] = [
  buildItinerary({
    id: 'new-one',
    name: 'New one',
    status: 'ready',
    recent: true,
    dateLabel: 'Edited 7 Oct 2026',
    editedAt: '2026-10-07T18:00:00+05:30',
    boardRank: 1,
    color: 'blue',
    speed: 33,
    accuracy: 28,
    width: 11,
    path: [point(16, 54), point(28, 46), point(42, 40), point(54, 32), point(66, 26), point(78, 18), point(88, 12)],
    labels: [
      { x: 58, y: 8, text: 'PREET VIHAR', sub: 'प्रीत विहार' },
      { x: 34, y: 28, text: 'MANDAWALI', sub: 'मंडावली' },
      { x: 78, y: 48, text: 'Sanjay Lake Park', sub: 'संजय लेक पार्क' },
    ],
    segments: [
      {
        fromLabel: 'Start point',
        toLabel: 'Stop 1',
        place: 'Patparganj Rd, New Delhi',
        distanceKm: 1.9,
        durationMin: 6,
        avgSpeedKmh: 33,
        fromIndex: 0,
        toIndex: 3,
      },
      {
        fromLabel: 'Stop 1',
        toLabel: 'Destination',
        place: '169, I.P.Extension, Patparganj, New Delhi',
        distanceKm: 1.8,
        durationMin: 5,
        avgSpeedKmh: 33,
        fromIndex: 3,
        toIndex: 6,
      },
    ],
    steps: NEW_ONE_STEPS,
    launches: [
      { atLabel: '7 Oct 2026, 10:24 AM', durationMin: 11, distanceKm: 3.7, avgSpeedKmh: 33, device: 'Samsung F42', appVersion: 'v1.2.3' },
      { atLabel: '3 Oct 2026, 06:12 PM', durationMin: 9, distanceKm: 3.6, avgSpeedKmh: 31, device: 'Samsung F42', appVersion: 'v1.2.3' },
      { atLabel: '28 Sep 2026, 09:18 AM', durationMin: 8, distanceKm: 3.5, avgSpeedKmh: 29, device: 'Samsung F42', appVersion: 'v1.2.2' },
      { atLabel: '21 Sep 2026, 07:45 PM', durationMin: 10, distanceKm: 4.0, avgSpeedKmh: 34, device: 'Samsung F42', appVersion: 'v1.2.1' },
    ],
    bars: [4, 2, 1, 2, 0],
    elevation: [
      { km: 0, meters: 205 },
      { km: 0.6, meters: 198 },
      { km: 1.2, meters: 220 },
      { km: 1.8, meters: 240 },
      { km: 2.4, meters: 255 },
      { km: 3.0, meters: 270 },
      { km: 3.7, meters: 286 },
    ],
  }),
  buildItinerary({
    id: 'uff',
    name: 'Uff',
    status: 'ready',
    recent: true,
    dateLabel: '7 Oct 2026',
    editedAt: '2026-10-07T12:00:00+05:30',
    boardRank: 2,
    color: 'orange',
    speed: 300,
    accuracy: 15,
    width: 8,
    path: [point(18, 58), point(36, 44), point(58, 36), point(82, 18)],
    segments: [
      {
        fromLabel: 'Start point',
        toLabel: 'Destination',
        place: 'Laxmi Nagar, New Delhi',
        distanceKm: 2.4,
        durationMin: 8,
        avgSpeedKmh: 300,
      },
    ],
    launchCount: 4,
    device: 'Pixel 8',
  }),
  buildItinerary({
    id: 'yshgs',
    name: 'Yshgs',
    status: 'locked',
    lockReason: 'Free limit',
    dateLabel: '19 Sep 2026',
    editedAt: '2026-09-19T11:20:00+05:30',
    boardRank: 3,
    color: 'slate',
    speed: 33,
    path: [point(22, 18), point(30, 32), point(48, 28), point(46, 48), point(70, 58)],
    segments: [
      { fromLabel: 'Start point', toLabel: 'Stop 1', place: 'Karol Bagh, New Delhi', distanceKm: 1.8, durationMin: 6, avgSpeedKmh: 33 },
      { fromLabel: 'Stop 1', toLabel: 'Stop 2', place: 'Rajendra Place, New Delhi', distanceKm: 1.6, durationMin: 6, avgSpeedKmh: 33 },
      { fromLabel: 'Stop 2', toLabel: 'Destination', place: 'Patel Nagar, New Delhi', distanceKm: 1.7, durationMin: 6, avgSpeedKmh: 33 },
    ],
    launchCount: 1,
    device: 'Samsung F42',
  }),
  buildItinerary({
    id: 'sund-kuchh',
    name: 'Sund kuchh',
    status: 'ready',
    dateLabel: '19 Sep 2026',
    editedAt: '2026-09-19T16:10:00+05:30',
    boardRank: 4,
    color: 'green',
    speed: 33,
    path: [point(14, 50), point(32, 36), point(50, 42), point(68, 24), point(86, 30)],
    segments: [
      { fromLabel: 'Start point', toLabel: 'Stop 1', place: 'Lajpat Nagar, New Delhi', distanceKm: 2.4, durationMin: 10, avgSpeedKmh: 33 },
      { fromLabel: 'Stop 1', toLabel: 'Stop 2', place: 'Defence Colony, New Delhi', distanceKm: 2.4, durationMin: 9, avgSpeedKmh: 33 },
      { fromLabel: 'Stop 2', toLabel: 'Destination', place: 'Greater Kailash, New Delhi', distanceKm: 2.4, durationMin: 9, avgSpeedKmh: 33 },
    ],
    launchCount: 3,
    device: 'OnePlus 12',
  }),
  buildItinerary({
    id: 'office-to-home',
    name: 'Office to Home',
    status: 'draft',
    dateLabel: '15 Sep 2026',
    editedAt: '2026-09-15T19:00:00+05:30',
    boardRank: 5,
    color: 'purple',
    speed: 40,
    path: [point(20, 22), point(40, 30), point(62, 26), point(84, 48)],
    segments: [
      { fromLabel: 'Office', toLabel: 'Stop 1', place: 'Connaught Place, New Delhi', distanceKm: 2.4, durationMin: 8, avgSpeedKmh: 40 },
      { fromLabel: 'Stop 1', toLabel: 'Home', place: 'Mayur Vihar, New Delhi', distanceKm: 2.4, durationMin: 7, avgSpeedKmh: 40 },
    ],
    launchCount: 0,
  }),
  buildItinerary({
    id: 'morning-ride',
    name: 'Morning Ride',
    status: 'ready',
    dateLabel: '10 Sep 2026',
    editedAt: '2026-09-10T07:40:00+05:30',
    boardRank: 6,
    color: 'green',
    speed: 36,
    mode: 'walking',
    path: [point(12, 40), point(28, 22), point(46, 30), point(64, 18), point(82, 34), point(90, 52)],
    segments: [
      { fromLabel: 'Home', toLabel: 'Park', place: 'Sanjay Lake, Delhi', distanceKm: 3.2, durationMin: 9, avgSpeedKmh: 36 },
      { fromLabel: 'Park', toLabel: 'Cafe', place: 'Preet Vihar, Delhi', distanceKm: 3.1, durationMin: 8, avgSpeedKmh: 36 },
      { fromLabel: 'Cafe', toLabel: 'Market', place: 'Mandawali, Delhi', distanceKm: 3.2, durationMin: 9, avgSpeedKmh: 36 },
      { fromLabel: 'Market', toLabel: 'Home', place: 'Patparganj, Delhi', distanceKm: 3.1, durationMin: 8, avgSpeedKmh: 36 },
    ],
    launchCount: 6,
    device: 'Pixel 8',
  }),
  buildItinerary({
    id: 'airport-dash',
    name: 'Airport dash',
    status: 'ready',
    recent: true,
    dateLabel: '20 Oct 2026',
    editedAt: '2026-10-20T09:00:00+05:30',
    boardRank: 7,
    color: 'red',
    speed: 42,
    path: [point(78, 16), point(60, 28), point(44, 36), point(24, 54)],
    segments: [
      { fromLabel: 'Home', toLabel: 'Ring road', place: 'Mayur Vihar Phase 1, Delhi', distanceKm: 9.2, durationMin: 13, avgSpeedKmh: 42 },
      { fromLabel: 'Ring road', toLabel: 'Airport', place: 'IGI Airport, Delhi', distanceKm: 9.2, durationMin: 13, avgSpeedKmh: 42 },
    ],
    launchCount: 2,
    device: 'iQOO Neo 9',
  }),
  buildItinerary({
    id: 'connaught-loop',
    name: 'Connaught loop',
    status: 'ready',
    dateLabel: '2 Oct 2026',
    editedAt: '2026-10-02T13:15:00+05:30',
    boardRank: 8,
    color: 'pink',
    speed: 28,
    path: [point(18, 36), point(36, 18), point(62, 20), point(78, 38), point(58, 56), point(30, 52)],
    segments: [
      { fromLabel: 'Gate', toLabel: 'Inner circle', place: 'Connaught Place, New Delhi', distanceKm: 2, durationMin: 8, avgSpeedKmh: 28 },
      { fromLabel: 'Inner circle', toLabel: 'Barakhamba', place: 'Barakhamba Road, New Delhi', distanceKm: 2.1, durationMin: 7, avgSpeedKmh: 28 },
      { fromLabel: 'Barakhamba', toLabel: 'Gate', place: 'Janpath, New Delhi', distanceKm: 2, durationMin: 7, avgSpeedKmh: 28 },
    ],
    launchCount: 5,
    device: 'Samsung F42',
  }),
  buildItinerary({
    id: 'market-hop',
    name: 'Market hop',
    status: 'draft',
    dateLabel: '28 Sep 2026',
    editedAt: '2026-09-28T17:45:00+05:30',
    boardRank: 9,
    color: 'brown',
    speed: 22,
    path: [point(16, 28), point(40, 24), point(62, 40), point(84, 34)],
    segments: [
      { fromLabel: 'Metro', toLabel: 'Market', place: 'Sarojini Nagar, New Delhi', distanceKm: 1.6, durationMin: 7, avgSpeedKmh: 22 },
      { fromLabel: 'Market', toLabel: 'INA', place: 'INA Market, New Delhi', distanceKm: 1.6, durationMin: 7, avgSpeedKmh: 22 },
    ],
    launchCount: 0,
  }),
  buildItinerary({
    id: 'noida-night',
    name: 'Noida night',
    status: 'ready',
    dateLabel: '12 Sep 2026',
    editedAt: '2026-09-12T21:10:00+05:30',
    boardRank: 10,
    color: 'orange',
    speed: 48,
    path: [point(12, 48), point(34, 40), point(56, 46), point(78, 22)],
    segments: [
      { fromLabel: 'Sector 18', toLabel: 'Atta', place: 'Sector 18, Noida', distanceKm: 3.1, durationMin: 6, avgSpeedKmh: 48 },
      { fromLabel: 'Atta', toLabel: 'Expressway', place: 'Noida Expressway', distanceKm: 3.1, durationMin: 6, avgSpeedKmh: 48 },
      { fromLabel: 'Expressway', toLabel: 'Film City', place: 'Film City, Noida', distanceKm: 3.2, durationMin: 7, avgSpeedKmh: 48 },
    ],
    launchCount: 2,
    device: 'OnePlus 12',
  }),
  buildItinerary({
    id: 'lake-circuit',
    name: 'Lake circuit',
    status: 'ready',
    dateLabel: '4 Sep 2026',
    editedAt: '2026-09-04T06:30:00+05:30',
    boardRank: 11,
    color: 'blue',
    speed: 30,
    mode: 'walking',
    path: [point(24, 56), point(40, 40), point(58, 48), point(76, 30)],
    segments: [
      { fromLabel: 'Gate', toLabel: 'Lake', place: 'Sanjay Lake Park, Delhi', distanceKm: 2.8, durationMin: 8, avgSpeedKmh: 30 },
      { fromLabel: 'Lake', toLabel: 'Metro', place: 'Mayur Vihar, Delhi', distanceKm: 2.8, durationMin: 8, avgSpeedKmh: 30 },
    ],
    launchCount: 1,
    device: 'Pixel 8',
  }),
  buildItinerary({
    id: 'west-ridge',
    name: 'West Ridge',
    status: 'ready',
    dateLabel: '1 Sep 2026',
    editedAt: '2026-09-01T15:00:00+05:30',
    boardRank: 12,
    color: 'slate',
    speed: 20,
    mode: 'point',
    path: [point(20, 20), point(34, 36), point(52, 30), point(70, 48), point(86, 40)],
    segments: [
      { fromLabel: 'Kamla Nagar', toLabel: 'Ridge', place: 'Kamla Nagar, Delhi', distanceKm: 2.2, durationMin: 10, avgSpeedKmh: 20 },
      { fromLabel: 'Ridge', toLabel: 'North campus', place: 'Delhi University, Delhi', distanceKm: 1.9, durationMin: 8, avgSpeedKmh: 20 },
    ],
    launchCount: 1,
    device: 'Samsung F42',
  }),
];

export function createItinerary(input: {
  id: string;
  name: string;
  description: string;
}): Itinerary {
  return buildItinerary({
    id: input.id,
    name: input.name.trim(),
    description: input.description,
    status: 'draft',
    recent: true,
    dateLabel: `Edited ${ITINERARY_TODAY}`,
    editedAt: '2026-10-25T10:47:00+05:30',
    boardRank: 0,
    color: 'blue',
    speed: 30,
    mode: 'driving',
    path: [point(18, 56), point(40, 42), point(62, 30), point(84, 16)],
    segments: [
      {
        fromLabel: 'Start point',
        toLabel: 'Stop 1',
        place: 'Patparganj Rd, New Delhi',
        distanceKm: 1.2,
        durationMin: 4,
        avgSpeedKmh: 30,
      },
      {
        fromLabel: 'Stop 1',
        toLabel: 'Destination',
        place: 'Mayur Vihar, New Delhi',
        distanceKm: 1.1,
        durationMin: 4,
        avgSpeedKmh: 30,
      },
    ],
    launchCount: 0,
    bars: [0, 0, 0, 0, 0],
  });
}

export function duplicateItinerary(item: Itinerary, id: string): Itinerary {
  const copy = JSON.parse(JSON.stringify(item)) as Itinerary;
  copy.id = id;
  copy.name = `${item.name} copy`;
  copy.status = item.status === 'locked' ? 'draft' : 'ready';
  copy.lockReason = undefined;
  copy.recent = true;
  copy.dateLabel = `Edited ${ITINERARY_TODAY}`;
  copy.editedAt = ITINERARY_NOW;
  copy.boardRank = 0;
  copy.segments = copy.segments.map((segment, index) => ({ ...segment, id: `${id}-seg-${index + 1}` }));
  copy.steps = copy.steps.map((step, index) => ({ ...step, id: `${id}-step-${index + 1}` }));
  copy.launches = copy.launches.map((launch, index) => ({ ...launch, id: `${id}-launch-${index + 1}` }));
  return copy;
}

export function touchItinerary(item: Itinerary): Itinerary {
  return {
    ...item,
    dateLabel: `Edited ${ITINERARY_TODAY}`,
    editedAt: ITINERARY_NOW,
    recent: true,
  };
}
